import { Environment } from '@/domainLogic/index.ts';
import { createCircuitBreaker } from '@/app/common/circuitBreaker.ts';
import type { CacheConfig } from '@lmn/lmn-commonlogic/dist/config';
import type { HeaderProps } from '@lmn/contenthub-shared/components/SharedHeader/services/types.d.ts';
import type { FooterProps } from '@lmn/contenthub-shared/components';
import { isEmptyObject } from '@/app/components/search/Utilities.ts';

type ContentHubEnvironment = { [env in Environment]: 'qa' | 'prod' };

export const contentHubEnvironment: ContentHubEnvironment = {
  [Environment.LOCAL]: 'prod',
  [Environment.QA]: 'qa',
  [Environment.PROD]: 'prod',
};

export const HEADER_FOOTER_TTL = 1000 * 60 * 5;

// Version-scoped cache keys: pods running a different header/footer props shape no
// longer read each other's Redis entries during Argo rollouts (stale-shape pollution).
// Manual constant because @lmn/contenthub-shared does not expose ./package.json in its
// exports map, so the installed version cannot be read at runtime.
// Bump when @lmn/contenthub-shared changes the header/footer props shape.
export const HEADER_FOOTER_CACHE_SCHEMA_VERSION = '2';

// Base prefixes kept for key listing (they match LKG and versioned keys too).
export const HEADER_CACHE_KEY_BASE_PREFIX = 'headerProps';
export const FOOTER_CACHE_KEY_BASE_PREFIX = 'footerProps';

export const HEADER_CACHE_KEY_PREFIX = `${HEADER_CACHE_KEY_BASE_PREFIX}-v${HEADER_FOOTER_CACHE_SCHEMA_VERSION}`;
export const FOOTER_CACHE_KEY_PREFIX = `${FOOTER_CACHE_KEY_BASE_PREFIX}-v${HEADER_FOOTER_CACHE_SCHEMA_VERSION}`;

export const HEADER_FOOTER_LKG_TTL = 1000 * 60 * 60 * 24; // 24 hours
export const HEADER_LKG_CACHE_KEY_PREFIX = `${HEADER_CACHE_KEY_BASE_PREFIX}-lkg-v${HEADER_FOOTER_CACHE_SCHEMA_VERSION}`;
export const FOOTER_LKG_CACHE_KEY_PREFIX = `${FOOTER_CACHE_KEY_BASE_PREFIX}-lkg-v${HEADER_FOOTER_CACHE_SCHEMA_VERSION}`;

/**
 * Returns true when a memory cache entry is absent or its TTL has expired.
 * Used as a fast synchronous gate before triggering a cache write.
 */
export const isEntryExpired = (entry: { cacheExpiryTime?: number } | undefined): boolean =>
  !entry?.cacheExpiryTime || entry.cacheExpiryTime < Date.now();

/**
 * Returns true when header props are valid: defined, non-empty, error-free AND matching
 * the current @lmn/contenthub-shared shape (navigationProps + globalVariables present).
 * Stale cache entries with an obsolete shape now fail validation instead of being
 * served silently (MPH-346 hardening).
 * Use as the single source of truth for deciding whether Content Hub returned good data.
 */
export const isHeaderPropsValid = (props: HeaderProps | undefined): props is HeaderProps =>
  !!props && !isEmptyObject(props) && !props.error && !!props.navigationProps && !!props.globalVariables;

/**
 * Returns true when footer props are valid: defined, non-empty, error-free AND matching
 * the current @lmn/contenthub-shared shape (footerProps + globalVariables present).
 * Stale cache entries with an obsolete shape now fail validation instead of being
 * served silently (MPH-346 hardening).
 * Use as the single source of truth for deciding whether Content Hub returned good data.
 */
export const isFooterPropsValid = (props: FooterProps | undefined): props is FooterProps =>
  !!props && !isEmptyObject(props) && !props.error && !!props.footerProps && !!props.globalVariables;

/**
 * Returns true when the LKG entry is not something the caller could fall back on: absent, expired,
 * or holding no usable props.
 *
 * The expiry alone is not enough. lmn-commonlogic's memoryCache stores an entry whether or not the
 * populate produced a value, so an LKG fallback read that finds nothing in Redis still leaves
 * `{ data: undefined, cacheExpiryTime: now + LKG TTL }` behind. A pod that cold-started during a
 * Content Hub outage would then look un-expired for the next 24h and never write its LKG again,
 * leaving it with no fallback for the following outage.
 */
export const isLkgUnusable = <T>(
  entry: { data?: T; cacheExpiryTime?: number } | undefined,
  isValid: (props: T | undefined) => boolean
): boolean => isEntryExpired(entry) || !isValid(entry?.data);

/**
 * Read-only CacheConfig for the LKG entry: writeCacheEntry populates it, so there is nothing for
 * getFromMemoryCache to create — it returns undefined on a cold miss.
 */
export const createLkgCacheConfig = (): CacheConfig => ({
  fallbackUrlQA: '',
  fallbackUrlProd: '',
  fallbackStaticFileName: '',
  createCacheValue: () => Promise.resolve(undefined),
  TTL: HEADER_FOOTER_LKG_TTL,
});

/** Number of consecutive CH failures before the circuit opens. */
export const CH_CIRCUIT_BREAKER_THRESHOLD = 5;
/** Time in ms the circuit stays open before allowing one probe request (half-open). */
export const CH_RESET_TIMEOUT_MS = 60_000;

/**
 * In-process circuit breaker for Content Hub calls.
 * Opens after CH_CIRCUIT_BREAKER_THRESHOLD consecutive failures; allows one probe
 * after CH_RESET_TIMEOUT_MS (half-open) to detect recovery.
 */
export const chCircuitBreaker = createCircuitBreaker({
  name: 'chCircuitBreakerState',
  threshold: CH_CIRCUIT_BREAKER_THRESHOLD,
  resetTimeoutMs: CH_RESET_TIMEOUT_MS,
});
